import { squareIndex } from './board';
import { deckSpecFor } from './cards';
import { epitaphPlays, epitaphSlots, handLimit, maxLinks } from './ledger/state';
import type { MatchState, Side } from './types';

/** Checks the invariants of §16.4 (plus a few structural ones). Returns violations; empty when valid. */
export function checkInvariants(s: MatchState): string[] {
  const out: string[] = [];
  const c = s.config.constants;

  // 1. Every card is in exactly one zone; per-side totals equal the match deck size.
  const seen = new Map<string, string>();
  const place = (id: string, zone: string) => {
    const prior = seen.get(id);
    if (prior) out.push(`card ${id} is in both ${prior} and ${zone}`);
    seen.set(id, zone);
    if (!s.cards[id]) out.push(`unknown card ${id} in ${zone}`);
  };
  for (const side of ['S', 'N'] as const) {
    const p = s.players[side];
    p.deck.forEach((id) => place(id, `${side}.deck`));
    p.hand.forEach((id) => place(id, `${side}.hand`));
    p.spent.forEach((id) => place(id, `${side}.spent`));
    p.removed.forEach((id) => place(id, `${side}.removed`));
    for (const id of [...p.deck, ...p.hand, ...p.spent, ...p.removed])
      if (s.cards[id] && s.cards[id]?.owner !== side)
        out.push(`card ${id} is in ${side}'s zones but not owned by ${side}`);
  }
  s.pile.forEach((e) => {
    if (e.source === 'card') place(e.cardId, 'pile');
  });
  const counts: Record<Side, number> = { S: 0, N: 0 };
  for (const id of seen.keys()) {
    const card = s.cards[id];
    if (card) counts[card.owner] += 1;
  }
  for (const side of ['S', 'N'] as const) {
    const expected = deckSpecFor(s.config, side).length;
    if (counts[side] !== expected)
      out.push(`${side} has ${counts[side]} cards in zones, expected ${expected}`);
  }
  if (Object.keys(s.cards).length !== seen.size) out.push('some cards are in no zone');

  // 2. Hand limit.
  for (const side of ['S', 'N'] as const)
    if (s.players[side].hand.length > handLimit(s, side))
      out.push(`${side} holds more than ${handLimit(s, side)} cards`);

  // 3. One pawn per square; square iff on board.
  const occupied = new Set<number>();
  for (const p of Object.values(s.pawns)) {
    if ((p.status === 'onBoard') !== (p.square !== null))
      out.push(`pawn ${p.id} square/status mismatch`);
    if (p.square) {
      const i = squareIndex(p.square);
      if (occupied.has(i)) out.push(`two pawns on one square (${p.id})`);
      occupied.add(i);
    }
  }
  for (const side of ['S', 'N'] as const)
    if (Object.values(s.pawns).filter((p) => p.side === side).length !== 6)
      out.push(`${side} does not have six pawns`);

  // 4. Epitaph slots and plays.
  for (const side of ['S', 'N'] as const) {
    const p = s.players[side];
    if (p.epitaphs.length > epitaphSlots(s, side)) out.push(`${side} holds too many Epitaphs`);
    if (p.epitaphPlays > epitaphPlays(s, side)) out.push(`${side} played too many Epitaphs`);
    if (p.epitaphs.some((e) => e.owner !== side))
      out.push(`${side} holds an Epitaph it does not own`);
  }

  // 5. A chain has 1..MAX_LINKS links, all moving the same pawn.
  if (s.chain) {
    const n = s.chain.links.length;
    if (n < 1 || n > maxLinks(s, s.chain.side)) out.push(`chain has ${n} links`);
    // A chain opened by an Epitaph (Séance Table) has replayed links before its cards.
    if (!s.chain.epitaphId && s.chain.cardIds.length !== n) out.push('chain card count mismatch');
    const pawn = s.pawns[s.chain.pawnId];
    if (!pawn || pawn.status !== 'onBoard' || pawn.side !== s.chain.side)
      out.push('chain pawn is not on the board');
    if (s.phase !== 'CHAIN') out.push(`chain exists in phase ${s.phase}`);
  } else if (s.phase === 'CHAIN') out.push('CHAIN phase without a chain');

  // 6. Scores are non-negative integers; totals equal the sum of completed rounds.
  for (const side of ['S', 'N'] as const) {
    const p = s.players[side];
    for (const [k, v] of [
      ['roundScore', p.roundScore],
      ['totalScore', p.totalScore],
    ] as const)
      if (!Number.isInteger(v) || v < 0) out.push(`${side}.${k} is not a non-negative integer`);
    const sum = s.rounds.reduce((acc, r) => acc + r.scores[side], 0);
    if (sum !== p.totalScore) out.push(`${side} total ${p.totalScore} ≠ sum of rounds ${sum}`);
    const wins = s.rounds.filter((r) => r.winner === side).length;
    if (wins !== p.roundWins) out.push(`${side} round wins mismatch`);
  }

  // 7. Round wins and round number bounds.
  for (const side of ['S', 'N'] as const)
    if (s.players[side].roundWins > c.ROUND_WINS_NEEDED)
      out.push(`${side} has too many round wins`);
  if (s.round < 1 || s.round > c.MAX_ROUNDS) out.push(`round ${s.round} out of range`);

  // Structural: phase bookkeeping.
  if (s.phase === 'MATCH_OVER' && !s.result) out.push('MATCH_OVER without a result');
  if (s.phase !== 'MATCH_OVER' && s.result) out.push('result set before MATCH_OVER');
  if (s.phase === 'CHOOSE_INSCRIPTION' && !s.pendingInscription)
    out.push('CHOOSE_INSCRIPTION without a pending inscription');
  if (s.phase === 'CHOOSE_EPITAPH' && !s.pendingEpitaphChoice)
    out.push('CHOOSE_EPITAPH without a pending choice');
  if (['TURN_START', 'ACTION', 'CHAIN'].includes(s.phase) && s.players[s.active].passed)
    out.push(`${s.active} acts after passing`);
  return out;
}
